import React, { useMemo, useRef } from 'react';
import Autoplay from 'embla-carousel-autoplay';

import CarouselPager from './AlarmVolume/CarouselPager';
import NoUnits from './AlarmVolume/NoUnits';
import UnassignedSummary from './AlarmVolume/UnassignedSummary';
import UnitTile from './AlarmVolume/UnitTile';
import { Card } from 'src/component/ds/ui/card';
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious
} from 'src/component/ds/ui/carousel';
import { TilesSkeleton } from './AlarmVolume/AlarmVolumeSkeleton';
import useAlarmVolume from './AlarmVolume/useAlarmVolume';
import useGridColumns from './AlarmVolume/useGridColumns';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import type { UnitAlarms } from './AlarmVolume/alarmVolume';

/** Narrowest a tile can be and still fit three three-digit badges on one line inside its padding. */
const MIN_TILE_WIDTH = 160;
const TILE_GAP = 8;
/** A page is never taller than this; more areas page sideways. */
const ROWS_PER_PAGE = 2;
/** Wraps from the last page back to the first, in both directions. */
const CAROUSEL_OPTS = { loop: true };

/**
 * The Alarm Volume card: a summary of how alarms sit across the facility, and a
 * tile per area. Stands in for the facility map when there is no map to draw.
 *
 * @param {object} props the card's props
 * @param {string} [props.className] classes merged onto the card
 * @param {boolean} props.loading whether the first per-area counts are still on their way
 * @returns {JSX.Element} the card
 */
export default function AlarmVolume ({ className, loading }: { className?: string; loading: boolean }) {
  const { t } = useTranslation();
  const { tiles, unassigned, selectedUnit, onSelectUnit } = useAlarmVolume();
  const [gridRef, columns] = useGridColumns(MIN_TILE_WIDTH, TILE_GAP, 4);
  const pageSize = columns * ROWS_PER_PAGE;
  const pages = useMemo(() => {
    const chunks: UnitAlarms[][] = [];
    for (let i = 0; i < tiles.length; i += pageSize) chunks.push(tiles.slice(i, i + pageSize));
    return chunks;
  }, [tiles, pageSize]);
  const paged = !loading && pages.length > 1;
  const noUnits = !loading && !tiles.length;
  const showUnassigned = !loading && !noUnits && unassigned.length > 0;
  const autoplay = useRef(Autoplay({
    delay: 8000, stopOnInteraction: false, stopOnMouseEnter: true, stopOnFocusIn: true
  }));

  const pageProps = {
    className: 'ds-grid ds-gap-2',
    style: { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` },
    'data-testid': 'alarm-volume-grid',
  };

  return (
    <Card className={cn('ds-shadow-sm', className)} data-testid='alarm-volume'>
      <Carousel
        opts={CAROUSEL_OPTS}
        plugins={[autoplay.current]}
        className='ds-flex ds-flex-col ds-gap-4 ds-p-4'
        aria-label={t('dashboard.alarmVolume')}
      >
        <div className='ds-flex ds-items-center ds-justify-between ds-gap-2'>
          <span className='ds-text-sm ds-font-normal ds-text-muted-foreground'>
            {t('dashboard.alarmVolume')}
          </span>
          <CarouselPager />
        </div>

        {noUnits && <NoUnits />}

        <div className='ds-flex ds-flex-col ds-gap-4 ds-narrow:ds-flex-row'>
          {showUnassigned && (
            <div className={cn(
              'ds-shrink-0 ds-border-border ds-pb-4 ds-border-b',
              'ds-narrow:ds-w-[170px] ds-narrow:ds-border-b-0 ds-narrow:ds-border-r ds-narrow:ds-pb-0 ds-narrow:ds-pr-4'
            )}>
              <UnassignedSummary counts={unassigned} />
            </div>
          )}

          <div ref={gridRef} className={cn('ds-relative ds-min-w-0 ds-flex-1', paged && 'ds-px-10', noUnits && 'ds-hidden')}>
            <CarouselContent className='ds-items-start'>
              {loading
                ? <CarouselItem {...pageProps}><TilesSkeleton count={pageSize} /></CarouselItem>
                : pages.map((pageTiles, idx) => (
                  <CarouselItem key={idx} {...pageProps}>
                    {pageTiles.map(({ unit, counts }) => (
                      <UnitTile
                        key={unit}
                        unit={unit}
                        counts={counts}
                        selected={unit === selectedUnit}
                        onSelect={onSelectUnit}
                      />
                    ))}
                  </CarouselItem>
                ))}
            </CarouselContent>
            {paged && (
              <>
                <CarouselPrevious
                  className='!ds-left-0'
                  aria-label={t('dashboard.alarmVolumePrevious')}
                  data-testid='alarm-volume-previous'
                />
                <CarouselNext
                  className='!ds-right-0'
                  aria-label={t('dashboard.alarmVolumeNext')}
                  data-testid='alarm-volume-next'
                />
              </>
            )}
          </div>
        </div>
      </Carousel>
    </Card>
  );
}
